import { PageHeader, PaginationPageDefault } from "@components";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { useRef, useState } from "react";
import { findPaginationFocusTarget } from "@/shared/components/pagination/pagination-base";
import {
	type ExpenseReportDateRangeValue,
	useGetExpenseConsolidatedByPlate,
	useGetExpensesReports,
} from "../hooks";
import { ExpensesConsolidatedByPlateTable } from "./expenses-consolidated-by-plate-table";
import { ExpensesDistributionChart } from "./expenses-distribution-chart";
import { ExpensesEvolutionChart } from "./expenses-evolution-chart";
import { ExpensesFilteredTotalSection } from "./expenses-filtered-total-section";
import { ExpensesReportsFilters } from "./expenses-reports-filters";
import { ExpensesReportsSummaryCards } from "./expenses-reports-summary-cards";

const now = today(getLocalTimeZone());
const startDate = new CalendarDate(now.year, 1, 1);
const CONSOLIDATED_PAGE_SIZE = 10;

const defaultDateRange: ExpenseReportDateRangeValue = {
	start: startDate,
	end: now,
};

export function ExpensesReportsTab() {
	const pageRef = useRef<HTMLDivElement>(null);
	const [dateRange, setDateRange] =
		useState<ExpenseReportDateRangeValue>(defaultDateRange);
	const [consolidatedByPlatePage, setConsolidatedByPlatePage] = useState(1);
	const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);

	const { data, isFetching: isFetchingReports } = useGetExpensesReports({
		startDate: dateRange?.start?.toString() ?? "",
		endDate: dateRange?.end?.toString() ?? "",
		VehicleIds: selectedVehicles,
	});

	const consolidatedCriteria = {
		startDate: dateRange?.start?.toString() ?? "",
		endDate: dateRange?.end?.toString() ?? "",
		PageSize: CONSOLIDATED_PAGE_SIZE,
		VehicleIds: selectedVehicles,
	};

	const consolidatedQuery = useGetExpenseConsolidatedByPlate({
		...consolidatedCriteria,
		PageNumber: consolidatedByPlatePage,
	});
	const {
		data: consolidatedByPlateData,
		isFetching: isFetchingConsolidated,
		isError: isConsolidatedError,
		refetch: refetchConsolidated,
	} = consolidatedQuery;

	const { total: consolidatedTotalPages } = usePaginationTotal({
		page: consolidatedByPlatePage,
		onPageChange: setConsolidatedByPlatePage,
		criteria: consolidatedCriteria,
		totalPages: consolidatedByPlateData?.items?.totalPages,
		query: consolidatedQuery,
	});

	const consolidatedItems = consolidatedByPlateData?.items?.items || [];

	const showConsolidatedFooter =
		!!consolidatedTotalPages && consolidatedTotalPages > 1;

	return (
		<div className="space-y-6" ref={pageRef}>
			<PageHeader subtitle="Relatórios e análises" title="Relatórios" />

			<ExpensesReportsFilters
				defaultDateRange={defaultDateRange}
				onDateRangeChange={(value) => {
					setDateRange(value);
					setConsolidatedByPlatePage(1);
				}}
				onSelectedVehiclesChange={(value) => {
					setSelectedVehicles(value);
					setConsolidatedByPlatePage(1);
				}}
			/>

			<section aria-labelledby="resumo-periodo-heading">
				<h2
					className="font-semibold text-lg text-primary leading-7"
					id="resumo-periodo-heading"
				>
					Resumo do Período
				</h2>

				<ExpensesReportsSummaryCards
					isLoading={isFetchingReports}
					items={data?.typeCards || []}
				/>
			</section>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<ExpensesEvolutionChart data={data?.monthlyEvolution || []} />
				<ExpensesDistributionChart data={data?.distributionByCategory || []} />
			</div>

			<ExpensesFilteredTotalSection
				expenseCount={data?.totals.expensesTotalCount ?? 0}
				totalAmount={data?.totals.expensesTotalAmount ?? 0}
			/>

			<ExpensesConsolidatedByPlateTable
				data={consolidatedItems}
				footer={
					showConsolidatedFooter ? (
						<PaginationPageDefault
							className="p-5"
							onPageChange={(page) => {
								setConsolidatedByPlatePage(page);
							}}
							page={consolidatedByPlatePage}
							total={consolidatedTotalPages}
						/>
					) : null
				}
				isError={isConsolidatedError}
				isLoading={isFetchingConsolidated}
				onRetry={() => {
					const nav = pageRef.current?.querySelector<HTMLElement>(
						"nav[aria-label='Paginação']"
					);
					const target = nav ? findPaginationFocusTarget(nav) : null;
					target?.focus({ preventScroll: true });
					refetchConsolidated();
				}}
			/>
		</div>
	);
}
